import { Frame } from "@/components/ui/frame";
import { cn } from "@/lib/utils";

type BadgeVariant =
  | "accent"
  | "danger"
  | "muted"
  | "raised"
  | "solid"
  | "success"
  | "transparent"
  | "warning";
type BadgeSize = "2xs" | "xs" | "sm" | "md" | "lg";

const variantClasses: Record<BadgeVariant, string> = {
  accent: "bg-sky-400/10 text-sky-400",
  danger: "bg-red-100 text-red-500 dark:bg-red-950",
  muted: "bg-gray-100 text-gray-1100 shadow-custom",
  raised: "bg-preview-bg text-gray-1100 shadow-custom",
  solid: "bg-gray-300 text-gray-1100",
  success: "bg-green-100 text-green-500 dark:bg-green-950",
  transparent: "bg-white/25 text-white backdrop-blur-md",
  warning: "bg-orange-100 text-orange-500 dark:bg-orange-950",
};

const sizeClasses: Record<BadgeSize, string> = {
  "2xs": "h-5 px-1.5 text-xs",
  lg: "h-11 px-5 text-base",
  md: "h-8 px-3 text-sm",
  sm: "h-7 px-2.5 text-sm",
  xs: "h-6 px-2 text-[13px]",
};

interface BadgeProps extends React.ComponentProps<"div"> {
  mono?: boolean;
  nested?: boolean;
  size?: BadgeSize;
  variant?: BadgeVariant;
}

export function Badge({
  variant = "raised",
  size = "sm",
  mono = true,
  nested = false,
  className,
  ...props
}: BadgeProps) {
  const pill = (
    <div
      className={cn(
        "flex w-fit select-none items-center justify-center gap-2 rounded-full",
        mono ? "font-berkeley-mono" : "font-[450]",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    />
  );

  return nested ? <Frame radius="pill">{pill}</Frame> : pill;
}
